/**
 * useLanguagePreference — the single place that changes the app language and persists it.
 *
 * On change it:
 *   1. calls `i18n.changeLanguage` (live UI switch),
 *   2. writes localStorage `eddkit.lang` (so the choice survives reload; the i18next
 *      detector reads it on next boot — highest priority),
 *   3. PATCHes `/api/profile` `{settings:{...existing, language}}` so the choice persists
 *      server-side and follows the user across devices/logins.
 *
 * Reading order on first load is owned by the i18next detector + AuthContext seeding:
 * localStorage → user.settings.language → browser → "en".
 */
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useProfile, useUpdateProfile } from "../api/auth";
import {
  LANG_STORAGE_KEY,
  normalizeLanguage,
  type AppLanguage,
} from "./index";

export function useLanguagePreference() {
  const { i18n } = useTranslation();
  const profileQ = useProfile();
  const updateProfile = useUpdateProfile();

  const current = normalizeLanguage(i18n.language);

  const setLanguage = useCallback(
    async (lang: AppLanguage): Promise<void> => {
      await i18n.changeLanguage(lang);
      try {
        localStorage.setItem(LANG_STORAGE_KEY, lang);
      } catch {
        /* private mode — localStorage unavailable; in-memory choice still applies */
      }
      // Persist server-side, merging into existing settings so nothing else is lost.
      const existing = profileQ.data?.settings ?? {};
      try {
        await updateProfile.mutateAsync({ settings: { ...existing, language: lang } });
      } catch {
        /* non-fatal: the local + localStorage choice already took effect */
      }
    },
    [i18n, profileQ.data?.settings, updateProfile],
  );

  return { current, setLanguage, saving: updateProfile.isPending };
}
